import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Wand2, Loader2, Upload, FileCheck2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { runGeneration } from "@/lib/generate";
import { GeneratingOverlay } from "@/components/GeneratingOverlay";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AGE_GROUPS, label } from "@/lib/activityData";

export default function MakeBilingual() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null); // {id, original_filename}
  const fileInput = useRef(null);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(data);
      toast.success(lang === "es" ? "Archivo subido." : "File uploaded.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!file && !text.trim()) { toast.error(lang === "es" ? "Ingresa una actividad o sube un archivo." : "Enter an activity or upload a file."); return; }
    setLoading(true);
    setElapsed(0);
    try {
      const payload = file ? { file_id: file.id, age_group: ageGroup } : { original_text: text, age_group: ageGroup };
      const j = await runGeneration("make-bilingual", payload, { onTick: setElapsed });
      toast.success(t("activity.saved"));
      navigate(`/activities/${j.result_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <GeneratingOverlay open={loading} elapsed={elapsed} />
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm font-semibold" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> {t("nav.dashboard")}
        </button>
        <div className="space-y-2">
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">{t("makeBilingual.title")}</h1>
          <p className="text-muted-foreground text-lg">{t("makeBilingual.subtitle")}</p>
        </div>

        <div className="space-y-6 rounded-3xl border border-border bg-card p-6 sm:p-8">
          <div className="space-y-2">
            <Label>{lang === "es" ? "Subir foto o documento (opcional)" : "Upload a photo or document (optional)"}</Label>
            <input ref={fileInput} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.txt" onChange={onUpload} className="hidden" data-testid="mb-file-input" />
            {file ? (
              <div className="flex items-center justify-between rounded-xl border border-primary/30 bg-muted px-4 py-3">
                <span className="flex items-center gap-2 text-sm font-medium truncate"><FileCheck2 className="h-4 w-4 text-primary" /> {file.original_filename}</span>
                <button onClick={() => setFile(null)} className="text-muted-foreground hover:text-foreground" data-testid="mb-clear-file"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <Button type="button" variant="outline" onClick={() => fileInput.current?.click()} disabled={uploading} className="rounded-xl h-12 w-full border-dashed" data-testid="mb-upload-btn">
                {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> {lang === "es" ? "Subiendo..." : "Uploading..."}</> : <><Upload className="h-4 w-4" /> {lang === "es" ? "Subir archivo (JPG, PNG, PDF, Word)" : "Upload file (JPG, PNG, PDF, Word)"}</>}
              </Button>
            )}
            <p className="text-xs text-muted-foreground">{lang === "es" ? "Los archivos se guardan de forma privada y segura." : "Files are stored privately and securely."}</p>
          </div>

          {!file && (
            <div className="space-y-2">
              <Label htmlFor="original">{t("makeBilingual.inputLabel")}</Label>
              <Textarea id="original" rows={8} value={text} onChange={(e) => setText(e.target.value)}
                placeholder={t("makeBilingual.inputPlaceholder")} className="rounded-xl resize-none" data-testid="mb-text" />
            </div>
          )}

          <div className="space-y-2">
            <Label>{t("makeBilingual.ageGroup")}</Label>
            <Select value={ageGroup} onValueChange={setAgeGroup}>
              <SelectTrigger className="h-12 rounded-xl" data-testid="mb-age"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {AGE_GROUPS.map((a) => <SelectItem key={a.value} value={a.value}>{label(a, lang)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={submit} disabled={loading} className="w-full py-3 rounded-xl bg-secondary hover:bg-secondary/90 text-base" data-testid="mb-submit">
            {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> {t("generator.generating")}</> : <><Wand2 className="h-5 w-5" /> {t("makeBilingual.transformBtn")}</>}
          </Button>
        </div>
      </main>
    </div>
  );
}

